import React, { useState, useEffect } from "react";
import Container from "./Container";
import faroLogo from "../assets/faro.png";

const navItems = [
  { id: "home", label: { en: "Home", es: "Inicio" } },
];

// Platform (login) lives on its own subdomain — the Azure-hosted app.
const PLATFORM_URL = "https://app.farolabs.dev";

const products = [
  {
    id: "rumbo",
    label: { en: "RUMBO", es: "RUMBO" },
    desc: {
      en: "The optimal trajectory of your merchandise",
      es: "La reposición óptima de tu mercadería",
    },
    icon: faroLogo,
  },
];

export default function Nav({ current, onNavigate, language, onLanguageChange }) {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  // Cerrar dropdown automáticamente al cambiar de ruta
  useEffect(() => {
    setProductsOpen(false);
  }, [current]);

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-white/80 backdrop-blur"
      style={{ borderBottomColor: "rgba(255,200,87,0.25)" }}>
      <Container className="flex h-16 items-center justify-between">
        {/* Brand */}
        <button
          type="button"
          onClick={() => onNavigate("home")}
          className="group -m-1 flex items-center gap-3 rounded-xl p-1 focus:outline-none focus:ring-2 focus:ring-amber-400"
          aria-label={language === "en" ? "Go to Home" : "Ir a Inicio"}
        >
          <img
            src={faroLogo}
            alt="FARO logo"
            className="h-8 w-8 object-contain"
            loading="lazy"
          />
          <span className="text-lg font-semibold tracking-tight text-slate-900 group-hover:text-[#0B1F3A]">
            FARO
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-2 md:flex">
          {navItems.map((n) => (
            <button
              key={n.id}
              onClick={() => onNavigate(n.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition hover:bg-amber-50 hover:text-[#0B1F3A] ${
                current === n.id ? "text-[#0B1F3A] bg-amber-50" : "text-slate-700"
              }`}
            >
              {n.label[language]}
            </button>
          ))}

          {/* Products dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setProductsOpen((v) => !v)}
              className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition hover:bg-amber-50 hover:text-[#0B1F3A] ${
                current === "rumbo" ? "text-[#0B1F3A] bg-amber-50" : "text-slate-700"
              }`}
            >
              {language === "en" ? "Products" : "Productos"}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className={`ml-0.5 transition-transform ${productsOpen ? "rotate-180" : ""}`}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {productsOpen && (
              <>
                {/* Backdrop invisible para cerrar el dropdown al clickear afuera */}
                <div
                  className="fixed inset-0 z-30"
                  onMouseDown={() => setProductsOpen(false)}
                />
                <div className="absolute top-full left-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-lg z-40">
                {products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setProductsOpen(false);
                      onNavigate(p.id);
                    }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition hover:bg-amber-50"
                  >
                    <img src={p.icon} alt="" className="h-8 w-8 rounded-full flex-shrink-0" />
                    <div>
                      <span className="block text-sm font-semibold text-slate-900">{p.label[language]}</span>
                      <span className="block text-xs text-slate-500">{p.desc[language]}</span>
                    </div>
                  </button>
                ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => onNavigate("about")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition hover:bg-amber-50 hover:text-[#0B1F3A] ${
              current === "about" ? "text-[#0B1F3A] bg-amber-50" : "text-slate-700"
            }`}
          >
            {language === "en" ? "About" : "Quiénes somos"}
          </button>

          <a
            href={PLATFORM_URL}
            className="rounded-full px-4 py-2 text-sm font-semibold text-[#0B1F3A] transition hover:bg-amber-50"
            style={{ border: "1px solid rgba(11,31,58,0.18)" }}
          >
            {language === "en" ? "Log in" : "Ingresar"}
          </a>

          <button
            onClick={() => onNavigate("contact")}
            className="ml-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition hover:opacity-90"
            style={{ background: "#FFC857", color: "#0B1F3A" }}
          >
            {language === "en" ? "Get Started" : "Comenzar"}
          </button>
        </nav>

        {/* Mobile menu button */}
        <button className="md:hidden" onClick={() => setOpen((v) => !v)} aria-label="Open menu">
          <div>
            <span className="block h-0.5 w-6 bg-slate-800"></span>
            <span className="mt-1 block h-0.5 w-6 bg-slate-800"></span>
            <span className="mt-1 block h-0.5 w-6 bg-slate-800"></span>
          </div>
        </button>
      </Container>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden">
          <Container className="pb-4">
            <div className="flex flex-col gap-2">
              {navItems.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    onNavigate(n.id);
                    setOpen(false);
                  }}
                  className={`rounded-xl px-3 py-2 text-left text-sm transition hover:bg-amber-50 ${
                    current === n.id ? "bg-amber-50 text-[#0B1F3A]" : "text-slate-700"
                  }`}
                >
                  {n.label[language]}
                </button>
              ))}

              {/* Products section mobile */}
              <div className="px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {language === "en" ? "Products" : "Productos"}
                </span>
                <div className="mt-2 flex flex-col gap-1">
                  {products.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        onNavigate(p.id);
                        setOpen(false);
                      }}
                      className="flex items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-amber-50"
                    >
                      <img src={p.icon} alt="" className="h-7 w-7 rounded-full flex-shrink-0" />
                      <div>
                        <span className="block text-sm font-semibold text-slate-900">{p.label[language]}</span>
                        <span className="block text-xs text-slate-500">{p.desc[language]}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => {
                  onNavigate("about");
                  setOpen(false);
                }}
                className={`rounded-xl px-3 py-2 text-left text-sm transition hover:bg-amber-50 ${
                  current === "about" ? "bg-amber-50 text-[#0B1F3A]" : "text-slate-700"
                }`}
              >
                {language === "en" ? "About" : "Quiénes somos"}
              </button>

              <a
                href={PLATFORM_URL}
                className="rounded-xl px-3 py-2 text-left text-sm font-semibold text-[#0B1F3A] transition hover:bg-amber-50"
                style={{ border: "1px solid rgba(11,31,58,0.18)" }}
              >
                {language === "en" ? "Log in" : "Ingresar"}
              </a>

              <button
                onClick={() => {
                  onNavigate("contact");
                  setOpen(false);
                }}
                className="rounded-xl px-3 py-2 text-left text-sm font-semibold transition hover:opacity-90"
                style={{ background: "#FFC857", color: "#0B1F3A" }}
              >
                {language === "en" ? "Get Started" : "Comenzar"}
              </button>
            </div>
          </Container>
        </div>
      )}
    </header>
  );
}